"use client";
import { jsxs, jsx } from "react/jsx-runtime";
import { useCriteo } from "../../../../utils/criteo/CriteoProvider.js";
import { Link } from "@nxt-costco-com/forge-components";
import { SpaceMd, SpaceXl } from "@nxt-costco-com/forge-design-tokens";
import { Analytics } from "../../../Analytics/index.js";
import { AdSetTitle } from "../../AdSet/AdSetTitle.js";
import { Observable } from "../ProductTileCarousel/Observable.js";
import { ProductTileCarouselClient } from "../ProductTileCarousel/ProductTileCarouselClient.js";
import { AdSetContainerStyles } from "../styles.js";
import { CostcoBox } from "../../../Util/CostcoBox.js";
import { useCheckScreen } from "../../../../hooks/useCheckScreen/index.js";
import { COMPONENT_NAME } from "./constants.js";
import { getLocalizedCriteoRenderingValue, isCommerceFlagshipFormat } from "./utils.js";
import { CommerceDisplayContainer, CommerceDisplayImage, CommerceDisplayBorderRadius, CommerceBannerSizingMap } from "./styles.js";
function CriteoCommerceDisplay({
  // eslint-disable-next-line react/prop-types
  contentStackEntry = {},
  // eslint-disable-next-line react/prop-types
  locale,
  // eslint-disable-next-line react/prop-types
  titleProps,
  // eslint-disable-next-line react/prop-types
  products,
  // eslint-disable-next-line react/prop-types
  placement,
  // eslint-disable-next-line react/prop-types
  analytics,
  /** @type {string[]} Array of parent SKUs for the carousel */
  // eslint-disable-next-line react/prop-types
  ParentSKUs = [],
  // eslint-disable-next-line react/prop-types
  format,
  // eslint-disable-next-line react/prop-types
  productTileSx,
  sx = {}
}) {
  var _a, _b, _c, _d;
  const { bdWHNumber } = useCriteo();
  const { isTablet, isDesktop, isMobile } = useCheckScreen();
  const { criteo_placement_name } = (contentStackEntry == null ? void 0 : contentStackEntry.third_party_curation) || {};
  const {
    product_card_display_style,
    show_reviews,
    show_action_button,
    inventory_aware_listings,
    product_tile_data_source
  } = (contentStackEntry == null ? void 0 : contentStackEntry.product_display_style) || {};
  if (!products || !placement) {
    return null;
  }
  const testId = `${COMPONENT_NAME}_${criteo_placement_name}`;
  const renderingInformation = placement == null ? void 0 : placement.rendering;
  const renderingType = isDesktop || isTablet ? "desktop" : "mobile";
  const bannerImageSrc = getLocalizedCriteoRenderingValue(
    renderingInformation,
    `${renderingType}_background_image`,
    locale
  );
  const bannerImageAltText = getLocalizedCriteoRenderingValue(
    renderingInformation,
    `${renderingType}_background_image_alt_text`,
    locale
  );
  const borderColor = getLocalizedCriteoRenderingValue(
    renderingInformation,
    `border_color`,
    locale
  );
  const maxProductTileImageWidth = "30%";
  const isFlagship = isCommerceFlagshipFormat(format) || isMobile;
  const isSmall = isFlagship ? isMobile || isTablet : isMobile;
  const fsMaxSlides = isTablet ? 2 : 3;
  const optionalFooterRedirectURL = getLocalizedCriteoRenderingValue(
    renderingInformation,
    `optional_footer_redirect_url`,
    locale
  );
  const MediaContainer = optionalFooterRedirectURL ? ({ sx: sx2, children }) => {
    var _a2;
    return /* @__PURE__ */ jsx(
      Link,
      {
        dataPrivate: (_a2 = contentStackEntry == null ? void 0 : contentStackEntry.enable_url_tracking) == null ? void 0 : _a2.disable_third_party_tracking,
        href: optionalFooterRedirectURL,
        sx: sx2,
        children
      }
    );
  } : ({ sx: sx2, children }) => /* @__PURE__ */ jsx(CostcoBox, { sx: sx2, children });
  let navigationButtonBottomOffset = "40%";
  if (!isFlagship) {
    navigationButtonBottomOffset = "55%";
  }
  if (isFlagship && isMobile) {
    navigationButtonBottomOffset = `-${SpaceXl}`;
  }
  if (isMobile) {
    navigationButtonBottomOffset = "2%";
  }
  const ariaMap = {
    "en-ca": {
      carouselLabel: "Sponsored Products",
      productTileNumberSeparator: "of"
    },
    "en-us": {
      carouselLabel: "Sponsored Products",
      productTileNumberSeparator: "of"
    },
    "fr-ca": {
      carouselLabel: "Produits commandités",
      productTileNumberSeparator: "sur"
    }
  };
  const carouselLabel = (_a = ariaMap[locale]) == null ? void 0 : _a.carouselLabel;
  const productTileNumberSeparator = (_b = ariaMap[locale]) == null ? void 0 : _b.productTileNumberSeparator;
  let bannerBorderStyles = {};
  if (isSmall) {
    bannerBorderStyles = {
      borderTopLeftRadius: CommerceDisplayBorderRadius,
      borderTopRightRadius: CommerceDisplayBorderRadius
    };
  } else {
    bannerBorderStyles = {
      borderBottomLeftRadius: CommerceDisplayBorderRadius,
      borderTopLeftRadius: CommerceDisplayBorderRadius
    };
  }
  let bannerMinWidth;
  let bannerMaxWidth;
  let maxContainerHeight;
  let extraProductTileSx = {};
  if (isFlagship) {
    bannerMinWidth = CommerceBannerSizingMap.flagship.minWidth;
    bannerMaxWidth = CommerceBannerSizingMap.flagship.maxWidth;
    maxContainerHeight = CommerceBannerSizingMap.flagship.containerHeight;
  } else {
    bannerMinWidth = CommerceBannerSizingMap.showcase.minWidth;
    bannerMaxWidth = CommerceBannerSizingMap.showcase.maxWidth;
    bannerMaxWidth = `clamp(${CommerceBannerSizingMap.showcase.minWidth}, 35%, ${CommerceBannerSizingMap.showcase.maxWidth})`;
    maxContainerHeight = CommerceBannerSizingMap.showcase.containerHeight;
    extraProductTileSx = {
      height: "100%"
    };
  }
  return /* @__PURE__ */ jsxs(CostcoBox, { sx: { ...AdSetContainerStyles, ...sx }, children: [
    /* @__PURE__ */ jsx(AdSetTitle, { ...titleProps }),
    /* @__PURE__ */ jsxs(
      CommerceDisplayContainer,
      {
        borderColor,
        "data-testid": testId,
        isMobile: isSmall,
        children: [
          /* @__PURE__ */ jsx(
            Observable,
            {
              observer: {
                // eslint-disable-next-line react/prop-types
                onClick: analytics == null ? void 0 : analytics.onBannerClicked
              },
              sx: {
                alignItems: "center",
                display: "flex",
                flex: 1,
                justifyContent: "center",
                maxHeight: maxContainerHeight,
                maxWidth: !isMobile ? bannerMaxWidth : "100%",
                minWidth: !isMobile ? bannerMinWidth : void 0,
                overflow: "hidden",
                width: isMobile ? "100%" : void 0,
                ...bannerBorderStyles
              },
              children: /* @__PURE__ */ jsx(
                MediaContainer,
                {
                  sx: {
                    maxHeight: maxContainerHeight,
                    alignItems: "center",
                    display: "flex",
                    flex: 1,
                    justifyContent: "center",
                    width: "100%"
                  },
                  children: /* @__PURE__ */ jsx(
                    Analytics,
                    {
                      analyticData: {
                        adItem: {
                          style: "banner",
                          title: bannerImageAltText
                        }
                      },
                      children: /* @__PURE__ */ jsx(
                        CommerceDisplayImage,
                        {
                          alt: bannerImageAltText,
                          isFlagship,
                          isMobile: isSmall,
                          loading: "lazy",
                          src: bannerImageSrc
                        },
                        bannerImageSrc
                      )
                    }
                  )
                }
              )
            }
          ),
          /* @__PURE__ */ jsx(
            CostcoBox,
            {
              sx: {
                flex: 1,
                // Necessary to convince children to respect width
                maxWidth: "100%",
                minWidth: 0
              },
              children: /* @__PURE__ */ jsx(
                Analytics,
                {
                  analyticData: {
                    adItem: {
                      style: "carousel",
                      title: (_c = contentStackEntry == null ? void 0 : contentStackEntry.ad_set_heading_and_costco_url) == null ? void 0 : _c.title,
                      type: "product"
                    },
                    doNotTrack: (_d = contentStackEntry == null ? void 0 : contentStackEntry.enable_url_tracking) == null ? void 0 : _d.disable_third_party_tracking,
                    title: contentStackEntry.title
                  },
                  children: /* @__PURE__ */ jsx(
                    ProductTileCarouselClient,
                    {
                      bdWHNumber,
                      containerSx: {
                        justifyContent: "center",
                        maxWidth: isSmall ? "100%" : void 0,
                        padding: !isSmall ? `0 ${SpaceMd}` : `0`
                      },
                      maxProductTileImageWidth: isFlagship ? maxProductTileImageWidth : void 0,
                      minProductTileImageWidth: isFlagship ? maxProductTileImageWidth : void 0,
                      navigationButtonBottomOffset,
                      productTileContainerSx: {
                        alignItems: "center",
                        display: "flex",
                        justifyContent: "center",
                        padding: 1
                      },
                      productTileNumberSeparator,
                      productTileSx: {
                        ...productTileSx,
                        ...extraProductTileSx,
                        marginBottom: 0,
                        maxWidth: !isSmall ? "480px" : "640px"
                      },
                      sx: {
                        alignItems: "center",
                        display: "flex",
                        height: "100%"
                      },
                      analytics,
                      ariaLabel: carouselLabel,
                      criteoDataSourceAttr: true,
                      disableBottomControls: isFlagship ? ParentSKUs.length <= 1 : !isMobile,
                      hideReviewStarValue: true,
                      inventoryAware: inventory_aware_listings,
                      isHorizontal: isFlagship,
                      itemNumbers: ParentSKUs,
                      locale,
                      maxSlides: isFlagship ? 1 : fsMaxSlides,
                      productTileMaxLineNumber: 1,
                      showActionButton: show_action_button,
                      showReviews: show_reviews,
                      testId,
                      variant: product_card_display_style,
                      navigationButtonsMobileDisplay: isMobile,
                      product_tile_data_source,
                      isCriteo: true
                    }
                  )
                }
              )
            }
          )
        ]
      }
    )
  ] });
}
export {
  CriteoCommerceDisplay
};
